"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays, MapPin } from "lucide-react";
import { SocialButtons } from "@/components/social-buttons";
import { defaultSiteSettings, type SiteSettings } from "@/lib/site-settings";

const navigationLinks = [
  { href: "/rendez-vous", label: "Rendez-vous" },
  { href: "/contact", label: "Contact" },
  { href: "/realisations", label: "Réalisations" },
  { href: "/avis", label: "Avis" },
  { href: "/espace-client", label: "Espace client" },
  { href: "/mes-demandes", label: "Mes demandes sans compte" },
];

const legalLinks = [
  { href: "/mentions-legales", label: "Mentions légales" },
  { href: "/confidentialite", label: "Confidentialité" },
  { href: "/conditions-reservation", label: "Réservation" },
  { href: "/cgv", label: "CGV" },
  { href: "/cookies", label: "Cookies" },
];

export function Footer() {
  const [legalEnabled, setLegalEnabled] = useState(true);
  const [privacyEnabled, setPrivacyEnabled] = useState(true);
  const [cookiesEnabled, setCookiesEnabled] = useState(true);
  const [reservationEnabled, setReservationEnabled] = useState(true);
  const [cgvEnabled, setCgvEnabled] = useState(true);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(defaultSiteSettings());

  useEffect(() => {
    let cancelled = false;
    const checkStatus = () => { void fetch("/api/legal/status", { cache: "no-store" }).then((response) => response.ok ? response.json() : null).then((data) => { if (cancelled) return; if (typeof data?.enabled === "boolean") setLegalEnabled(data.enabled); if (typeof data?.privacyEnabled === "boolean") setPrivacyEnabled(data.privacyEnabled); if (typeof data?.cookiesEnabled === "boolean") setCookiesEnabled(data.cookiesEnabled); if (typeof data?.reservationEnabled === "boolean") setReservationEnabled(data.reservationEnabled); if (typeof data?.cgvEnabled === "boolean") setCgvEnabled(data.cgvEnabled); }).catch(() => undefined); };
    checkStatus();
    const interval = window.setInterval(checkStatus, 5000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, []);

  useEffect(() => { void fetch("/api/site-settings", { cache: "no-store" }).then((response) => response.ok ? response.json() : null).then((data) => { if (data?.settings) setSiteSettings(data.settings); }).catch(() => undefined); }, []);

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-grid">
          <section className="footer-intro" aria-label="Présentation">
            <a className="footer-location" href={siteSettings.locationUrl} target="_blank" rel="noreferrer"><MapPin /> {siteSettings.city}</a>
            <h2>{siteSettings.societyName}</h2>
            <p>{siteSettings.description}</p>
            <div className="footer-intro-actions">
              <Link className="footer-book-link" href="/rendez-vous"><CalendarDays /> Prendre rendez-vous</Link>
              <div className="footer-social-actions"><SocialButtons settings={siteSettings} /></div>
            </div>
          </section>
          <nav className="footer-column" aria-label="Navigation du site">
            <h3>Découvrir</h3>
            <div className="footer-link-list">{navigationLinks.map((link) => <Link key={link.href} href={link.href}>{link.label}<ArrowUpRight /></Link>)}</div>
          </nav>
          <nav className="footer-column" aria-label="Informations légales">
            <h3>Informations</h3>
            <div className="footer-link-list">{legalLinks.filter((link) => (link.href !== "/mentions-legales" || legalEnabled) && (link.href !== "/confidentialite" || privacyEnabled) && (link.href !== "/cookies" || cookiesEnabled) && (link.href !== "/conditions-reservation" || reservationEnabled) && (link.href !== "/cgv" || cgvEnabled)).map((link) => <Link key={link.href} href={link.href}>{link.label}<ArrowUpRight /></Link>)}</div>
          </nav>
        </div>
        <div className="footer-bottom"><span>© {new Date().getFullYear()} {siteSettings.societyName}<span className="footer-credit">Développé par Mike Martini</span></span></div>
      </div>
    </footer>
  );
}
